import { useEffect, useState } from 'react';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { surveyRespondentsApi } from '../surveyApi';
import { SURVEY_QUESTION_TYPE, type SurveyQuestionnaireSummary, type SurveySummaryQuestion } from '../surveyTypes';
import { SurveyBarChart } from './SurveyBarChart';
import { SurveyTurnout } from './SurveyTurnout';
import { useQuestionnaireOptions } from './useQuestionnaireOptions';

/** A written answer as it reads — a date formatted in the browser, everything else as given. */
function writtenAnswer(q: SurveySummaryQuestion, text: string): string {
  return q.type === SURVEY_QUESTION_TYPE.Date ? formatDateOnly(text) : text;
}

/**
 * Responses › Summary — one questionnaire, every question with its chart or its answers.
 *
 * Choice, Scale and Division questions draw `<SurveyBarChart>`; Short and Long paragraph, Date
 * and Time list what was written, newest first; a Name question says how many it names. Counts
 * are of every live response; written answers only those the reader is cleared for, and the rest
 * are counted out loud rather than silently missing.
 */
export function SurveySummaryView({ surveyId }: { surveyId: string }) {
  const questionnaires = useQuestionnaireOptions(surveyId);
  const [summary, setSummary] = useState<SurveyQuestionnaireSummary | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  // The first questionnaire until the reader picks another — derived, not set in an effect.
  const questionnaire = questionnaires?.[0];
  const questionnaireId = questionnaire?.id ?? '';

  useEffect(() => {
    if (!questionnaireId) return;

    const controller = new AbortController();

    surveyRespondentsApi
      .summary(surveyId, questionnaireId, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;

        setSummary(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the summary.', 0));
      });

    return () => controller.abort();
  }, [surveyId, questionnaireId]);

  if (questionnaires === null) return <Skeleton className="m-4 h-40" />;

  if (questionnaires.length === 0) {
    return <EmptyState message="This survey has no questionnaire yet. Create one under Configuration." />;
  }

  return (
    /*
      The Configuration › Questionnaire view's design, exactly (product call, 09 Oct 2026): a
      column HALF the panel's width and centred (full width below `lg`, where half would squeeze
      the bar labels), one padded `<ProfileCard>`, the questionnaire's title in the
      brand-topped header card, and each question in its own numbered card — the two tabs read
      as the same document, asked and answered.
    */
    <div className="modern-scrollbar h-full min-h-0 overflow-y-auto overflow-x-hidden p-4">
      <div className="mx-auto flex w-full flex-col gap-4 lg:w-1/2">
        <ProfileCard id="survey-profile-summary" title="Summary" count={summary?.responseCount} padded>
          <div className="flex flex-col gap-3">
            {/* The survey's ONE questionnaire, named — not picked. */}
            <div className="rounded-xl border border-slate-200 border-t-4 border-t-brand-500 bg-white p-5">
              <h2 className="text-lg text-slate-800">{questionnaire?.title}</h2>
              {questionnaire?.description ? (
                <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{questionnaire.description}</p>
              ) : null}
              {summary ? (
                <div className="mt-3">
                  <SurveyTurnout summary={summary} />
                </div>
              ) : null}
            </div>

            {error ? (
              <ErrorState message={error.message} correlationId={error.correlationId} />
            ) : !summary ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <>
                {summary.questions.length === 0 ? (
                  <p className="px-1 text-xs text-slate-500">This questionnaire has no questions.</p>
                ) : null}

                {summary.questions.map((q, index) => (
                  <section
                    key={q.questionId}
                    className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5"
                  >
                    <div className="flex gap-2">
                      <span className="shrink-0 font-mono text-sm tabular-nums text-slate-500">{index + 1}.</span>
                      <div className="flex min-w-0 flex-col gap-1">
                        <h3 className="text-sm text-slate-800">
                          {q.text}
                          {q.isRequired ? <span className="ml-1 text-red-600">*</span> : null}
                        </h3>
                        {q.description ? <p className="text-xs text-slate-500">{q.description}</p> : null}
                      </div>
                    </div>

                    {/*
                      EACH question's own turnout (product call, 12 Oct 2026): how many of the
                      targeted employees answered THIS question — an optional one is skipped by
                      some. It replaces the bare "N responses"; answers from outside the target
                      are the difference, said apart.
                    */}
                    <SurveyTurnout
                      compact
                      summary={{
                        targetedCount: summary.targetedCount,
                        targetedRespondedCount: q.targetedAnsweredCount,
                        outsideTargetCount: q.answeredCount - q.targetedAnsweredCount,
                      }}
                    />

                    {q.type === SURVEY_QUESTION_TYPE.Scale ? (
                      // The rating's average, over every response — read against the top of its scale.
                      <p className="text-sm text-slate-600">
                        Average rating{' '}
                        {q.average != null ? (
                          <>
                            <span className="font-mono font-medium tabular-nums text-slate-800">
                              {q.average.toFixed(2)}
                            </span>
                            {q.scaleMax != null ? <span className="text-slate-500"> of {q.scaleMax}</span> : null}
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </p>
                    ) : null}

                    {q.bars.length > 0 ? (
                      <SurveyBarChart
                        bars={q.bars}
                        total={q.answeredCount}
                        label={`Answers to question ${index + 1}`}
                      />
                    ) : q.type === SURVEY_QUESTION_TYPE.Name ? (
                      <p className="text-sm text-slate-500">Who answered is listed under Assessment.</p>
                    ) : q.texts.length > 0 || q.withheldCount > 0 ? (
                      <div className="flex flex-col gap-2">
                        {q.texts.length > 0 ? (
                          <ul className="modern-scrollbar flex max-h-64 flex-col divide-y divide-slate-100 overflow-y-auto rounded-lg bg-slate-50">
                            {q.texts.map((text, i) => (
                              <li
                                key={i}
                                className={`whitespace-pre-line px-3 py-2 text-sm text-slate-600 ${
                                  q.type === SURVEY_QUESTION_TYPE.Date || q.type === SURVEY_QUESTION_TYPE.Time
                                    ? 'font-mono'
                                    : ''
                                }`}
                              >
                                {writtenAnswer(q, text)}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {q.withheldCount > 0 ? (
                          <p className="text-xs text-slate-500">
                            {q.withheldCount} {q.withheldCount === 1 ? 'answer is' : 'answers are'} withheld — from
                            employees above your access level.
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No answers yet.</p>
                    )}
                  </section>
                ))}
              </>
            )}
          </div>
        </ProfileCard>
      </div>
    </div>
  );
}
